import openai from './openaiClient';
import { userService } from './userService';
import { supabase } from '../lib/supabase';


/**
 * Track "Start Planning Your Trip" button click
 */
export const trackButtonClick = async (userId = null) => {
  try {
    const sessionId = sessionStorage?.getItem('session_id') || crypto?.randomUUID();
    sessionStorage?.setItem('session_id', sessionId);

    const { error } = await supabase?.from('trip_planning_button_clicks')?.insert({
        user_id: userId,
        session_id: sessionId,
        clicked_at: new Date()?.toISOString()
      });

    if (error) {
      console.error('Error tracking button click:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error tracking button click:', error);
    return { success: false, error };
  }
};

/**
 * Generates AI-powered trip planning responses using OpenAI API with user preferences
 * @param {string} userMessage - The user's input message.
 * @param {Array} conversationHistory - Previous messages for context.
 * @param {string} userId - Optional user ID to fetch preferences
 * @returns {Promise<string>} The AI assistant's response.
 */
export async function generateTripPlanningResponse(userMessage, conversationHistory = [], userId = null) {
  try {
    // Validate API key before making request
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    if (!apiKey || apiKey === 'your_vite_openai_api_key' || apiKey?.trim() === '') {
      throw new Error('OpenAI API key is not configured properly. Please add your API key to the .env file.');
    }

    // Fetch user preferences if userId is provided
    let userPreferences = null;
    if (userId) {
      try {
        const prefsData = await userService?.getUserPreferences(userId);
        userPreferences = prefsData;
      } catch (error) {
        console.log('Could not fetch user preferences, continuing without them:', error);
      }
    }

    // Build personalized system prompt based on user preferences
    const baseSystemPrompt = `You are an expert state park trip planning assistant. You provide detailed, helpful recommendations for state park adventures including:

- Specific state park recommendations with detailed information
- Camping and accommodation options
- Activity suggestions and difficulty levels
- Budget planning and cost breakdowns
- Best times to visit and weather considerations
- What to pack and prepare
- Local attractions and dining options
- Safety tips and park regulations
- Official state park websites and reservation links

Always provide practical, actionable advice with specific details like park names, facilities, costs, and activities. Format your responses with clear sections using markdown formatting with **bold headers** and bullet points for easy reading.

When discussing specific parks, include:
- Park location and key features
- Available activities and difficulty levels
- Facilities and amenities
- Reservation requirements and fees
- Best times to visit
- What to pack specific to that park
- Official website links for reservations and information

Be enthusiastic and encouraging while maintaining accuracy and helpfulness.`;

    // Add personalized context if preferences exist
    let personalizedPrompt = baseSystemPrompt;
    if (userPreferences && Object?.keys(userPreferences)?.length > 0) {
      personalizedPrompt += `\n\n**USER TRAVEL PREFERENCES** (personalize recommendations based on these):\n`;
      
      if (userPreferences?.preferredParkTypes?.length > 0) {
        personalizedPrompt += `- **Preferred Park Types**: ${userPreferences?.preferredParkTypes?.join(', ')}\n`;
      }
      
      if (userPreferences?.activityInterests?.length > 0) {
        personalizedPrompt += `- **Activity Interests**: ${userPreferences?.activityInterests?.join(', ')}\n`;
      }
      
      if (userPreferences?.accommodationStyle) {
        personalizedPrompt += `- **Accommodation Style**: ${userPreferences?.accommodationStyle}\n`;
      }
      
      if (userPreferences?.groupSize) {
        personalizedPrompt += `- **Typical Group Size**: ${userPreferences?.groupSize}\n`;
      }
      
      if (userPreferences?.budgetRange) {
        personalizedPrompt += `- **Budget Range**: ${userPreferences?.budgetRange}\n`;
      }
      
      if (userPreferences?.travelSeason?.length > 0) {
        personalizedPrompt += `- **Preferred Travel Seasons**: ${userPreferences?.travelSeason?.join(', ')}\n`;
      }
      
      if (userPreferences?.difficultyLevel) {
        personalizedPrompt += `- **Activity Difficulty Preference**: ${userPreferences?.difficultyLevel}\n`;
      }
      
      if (userPreferences?.accessibilityNeeds?.length > 0) {
        personalizedPrompt += `- **Accessibility Requirements**: ${userPreferences?.accessibilityNeeds?.join(', ')}\n`;
      }
      
      personalizedPrompt += `\n**IMPORTANT**: Use these preferences to personalize your recommendations. Suggest parks, activities, and accommodations that match the user's stated preferences. If the user asks for something different from their preferences, prioritize their current request but mention how it relates to their saved preferences.`;
    }

    // Build conversation context
    const messages = [
      {
        role: 'system',
        content: personalizedPrompt
      },
      ...conversationHistory?.map(msg => ({
        role: msg?.isUser ? 'user' : 'assistant',
        content: msg?.content
      })),
      {
        role: 'user',
        content: userMessage
      }
    ];

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: messages,
      max_tokens: 2000,
      temperature: 0.7,
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    console.error('Error generating trip planning response:', error);
    
    // Provide specific error messages based on error type
    let errorMessage = `I apologize, but I'm having trouble connecting to my knowledge base right now.`;
    
    if (error?.status === 401 || error?.code === 'invalid_api_key') {
      errorMessage = `🔑 **API Key Issue Detected**

Your OpenAI API key appears to be invalid or has been revoked. 

**To fix this:**
1. Get a new API key from [OpenAI Dashboard](https://platform.openai.com/api-keys)
2. Update your \`.env\` file with the new key:
   \`\`\`
   VITE_OPENAI_API_KEY=sk-proj-your-new-key-here
   \`\`\`
3. **Restart your development server** (stop and run \`npm run start\` again)
4. **Clear your browser cache** and reload this page

**Need Help?** Check [OpenAI API Documentation](https://platform.openai.com/docs) for more details.`;
    } else if (error?.status === 429) {
      errorMessage = `⚠️ **Rate Limit Exceeded**

You've hit the OpenAI API rate limit. This usually means:
- Too many requests in a short time
- Your account's usage quota is exceeded

**Solutions:**
- Wait a few minutes and try again
- Check your [OpenAI Usage Dashboard](https://platform.openai.com/usage)
- Upgrade your OpenAI plan for higher limits`;
    } else if (error?.code === 'ECONNREFUSED' || error?.message?.includes('network')) {
      errorMessage = `🌐 **Network Connection Issue**

Cannot reach OpenAI servers. Please check:
- Your internet connection
- Firewall or VPN settings
- OpenAI service status at [status.openai.com](https://status.openai.com)`;
    } else if (error?.message?.includes('not configured')) {
      errorMessage = error?.message;
    }
    
    // Return user-friendly fallback with error details
    return `${errorMessage}

---

**General State Park Planning Tips** (while we fix the connection):

**Popular Activities:**
- Hiking trails and nature walks
- Camping (tent, RV, or cabin rentals)
- Wildlife viewing and photography
- Water activities (swimming, kayaking, fishing)

**Planning Checklist:**
- Reserve campsites/accommodations 2-3 weeks in advance
- Check weather forecasts before departure
- Pack appropriate gear and clothing layers
- Bring cash for park fees and local vendors
- Download offline maps of the area

Please fix the API connection issue above and try your question again!`;
  }
}

/**
 * **ENHANCED**: Comprehensive trip summary with location-specific images, park websites, and user preferences
 * @param {string} userMessage - The user's input message.
 * @param {Array} conversationHistory - Previous messages for context.
 * @param {string} userId - Optional user ID to fetch preferences
 * @returns {Promise<Object|null>} Complete detailed trip data or null if not applicable.
 */
export async function generateTripSummaryData(userMessage, conversationHistory = [], userId = null) {
  try {
    // Fetch user preferences if userId is provided
    let userPreferences = null;
    if (userId) {
      try {
        const prefsData = await userService?.getUserPreferences(userId);
        userPreferences = prefsData;
      } catch (error) {
        console.log('Could not fetch user preferences for trip summary:', error);
      }
    }

    // **ENHANCED**: More flexible detection logic
    const messageText = userMessage?.toLowerCase() || '';
    const conversationText = conversationHistory?.map(msg => msg?.content?.toLowerCase())?.join(' ') || '';
    
    const planningKeywords = [
      'plan', 'trip', 'visit', 'go to', 'weekend', 'vacation', 'holiday',
      'itinerary', 'schedule', 'days', 'week', 'month', 'summer', 'winter',
      'camping', 'hiking', 'park', 'outdoor', 'adventure', 'travel',
      '2 day', '3 day', 'two day', 'three day', 'overnight', 'stay',
      'book', 'reserve', 'confirm', 'ready', 'sounds good', 'perfect',
      'family trip', 'group', 'friends', 'together', 'we want',
      'help me', 'looking for', 'recommend', 'suggest', 'best',
      'accommodation', 'campground', 'cabin', 'hotel', 'lodge'
    ];

    const locationKeywords = [
      'state park', 'national park', 'forest', 'lake', 'mountain', 'beach',
      'trail', 'campground', 'cabin', 'tent', 'rv', 'nature', 'outdoor'
    ];

    const detailKeywords = [
      'how much', 'cost', 'price', 'budget', 'when', 'where', 'what to',
      'activities', 'do', 'see', 'explore', 'experience', 'accommodation'
    ];

    // Check if message contains planning intent
    const hasPlanningIntent = planningKeywords?.some(keyword => 
      messageText?.includes(keyword) || conversationText?.includes(keyword)
    );
    
    const hasLocationContext = locationKeywords?.some(keyword => 
      messageText?.includes(keyword) || conversationText?.includes(keyword)
    );
    
    const hasDetailDiscussion = detailKeywords?.some(keyword => 
      messageText?.includes(keyword) || conversationText?.includes(keyword)
    );

    // **MORE RESPONSIVE**: Lower threshold for trip summary generation
    const shouldGenerateSummary = 
      // Explicit planning requests
      messageText?.includes('itinerary') ||
      messageText?.includes('plan my trip') ||
      messageText?.includes('start planning') ||
      messageText?.includes('book') ||
      messageText?.includes('confirm') ||
      messageText?.includes('ready') ||
      messageText?.includes('sounds good') ||
      messageText?.includes('perfect') ||
      messageText?.includes('let\'s do') ||
      // After any exchange with planning context (lowered threshold)
      (conversationHistory?.length >= 1 && hasPlanningIntent && (hasLocationContext || hasDetailDiscussion)) ||
      // Direct time-based requests
      (messageText?.match(/\d+\s*(day|week|night)/)) ||
      // Location + activity combinations
      (hasLocationContext && hasDetailDiscussion) ||
      // General travel inquiry with context
      (conversationHistory?.length >= 1 && hasPlanningIntent);

    if (!shouldGenerateSummary) {
      return null;
    }

    // **CRITICAL FIX**: Build comprehensive conversation summary for context extraction
    const conversationContext = conversationHistory?.map(msg => 
      `${msg?.isUser ? 'User' : 'Assistant'}: ${msg?.content}`
    )?.join('\n') + `\nUser: ${userMessage}`;

    // Build personalized prompt for trip generation
    let tripGenerationPrompt = `Based on this ENTIRE conversation, generate a complete detailed trip plan that reflects the SPECIFIC parks, activities, duration, budget, and preferences discussed:

${conversationContext}`;

    // Add user preferences context
    if (userPreferences && Object?.keys(userPreferences)?.length > 0) {
      tripGenerationPrompt += `\n\n**USER'S SAVED TRAVEL PREFERENCES** (incorporate these into the trip plan):\n`;
      
      if (userPreferences?.preferredParkTypes?.length > 0) {
        tripGenerationPrompt += `- Preferred Park Types: ${userPreferences?.preferredParkTypes?.join(', ')}\n`;
      }
      
      if (userPreferences?.activityInterests?.length > 0) {
        tripGenerationPrompt += `- Activity Interests: ${userPreferences?.activityInterests?.join(', ')}\n`;
      }
      
      if (userPreferences?.accommodationStyle) {
        tripGenerationPrompt += `- Accommodation Style: ${userPreferences?.accommodationStyle}\n`;
      }
      
      if (userPreferences?.groupSize) {
        tripGenerationPrompt += `- Typical Group Size: ${userPreferences?.groupSize}\n`;
      }
      
      if (userPreferences?.budgetRange) {
        tripGenerationPrompt += `- Budget Range: ${userPreferences?.budgetRange}\n`;
      }
      
      if (userPreferences?.travelSeason?.length > 0) {
        tripGenerationPrompt += `- Preferred Travel Seasons: ${userPreferences?.travelSeason?.join(', ')}\n`;
      }
      
      if (userPreferences?.difficultyLevel) {
        tripGenerationPrompt += `- Activity Difficulty Preference: ${userPreferences?.difficultyLevel}\n`;
      }
      
      if (userPreferences?.accessibilityNeeds?.length > 0) {
        tripGenerationPrompt += `- Accessibility Requirements: ${userPreferences?.accessibilityNeeds?.join(', ')}\n`;
      }
    }

    tripGenerationPrompt += `\n\n**CRITICAL INSTRUCTIONS FOR UNIQUE TRIP GENERATION:**

1. **Extract Specific Details from Conversation:**
   - Use the EXACT state park names mentioned in the conversation
   - Use the EXACT duration discussed (number of days/nights)
   - Use the EXACT budget range mentioned
   - Use the EXACT group size discussed
   - Use the EXACT activities and interests mentioned

2. **Create Unique, Conversation-Specific Content:**
   - Title should reflect the specific parks or region discussed
   - Highlights must be based on actual conversation topics, NOT generic statements
   - Activities must match what was discussed (e.g., if user mentioned "waterfalls," include waterfall hikes)
   - Location must be the specific park(s) or area mentioned

3. **Generate Comprehensive Trip Structure:**
   - Basic trip information aligned with conversation details
   - Detailed accommodation options matching discussed preferences
   - Complete daily itinerary with:
     * Day title and date
     * Location-appropriate state park images from Unsplash
     * Descriptive alt text matching actual locations
     * Detailed activities with proper time formats (9:00 AM, 2:30 PM)
     * Activities matching conversation topics
     * Driving directions and logistics
   - Comprehensive accommodation details
   - Dining options appropriate for discussed group size
   - Activity types from conversation
   - Weather forecast for mentioned seasons
   - State park websites and reservation info
   - Budget estimates matching discussion
   - Accessibility accommodations if needed

4. **Time Format Requirements:**
   - All "time" fields must use formats: "9:00 AM", "2:30 PM", or "14:30"
   - NEVER use "Morning", "Afternoon", "Evening" in time fields

5. **Image Requirements:**
   - Use high-quality Unsplash images matching specific park types discussed
   - Alt text must describe actual locations mentioned
   - Format: https://images.unsplash.com/photo-[id]?auto=format&fit=crop&w=1200&q=80

6. **DO NOT use generic placeholders:**
   - ❌ "Your State Park Adventure" - ❌"Multiple Parks" - ❌"Scenic hiking trails with waterfall views" (unless waterfalls were specifically discussed)
   - ✅ Use actual park names, specific activities, and real details from conversation

**Example of GOOD vs BAD:**
BAD: "Your State Park Adventure" / "Multiple Parks" / "Moderate budget" GOOD:"Yellowstone & Grand Teton 5-Day Adventure" / "Yellowstone National Park, Grand Teton National Park" / "$800-1200 (discussed $1000 budget)"`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        {
          role: 'system',
          content: `You are an expert state park trip planner. You MUST generate trip summaries that are UNIQUE and SPECIFIC to each conversation.

**CRITICAL RULES:**
1. **Read the ENTIRE conversation** to extract specific details
2. **Use ACTUAL park names, locations, and activities** mentioned in the conversation
3. **DO NOT use generic placeholders** like "Your State Park Adventure" or "Multiple Parks"
4. **Highlights MUST be conversation-specific**, not generic statements
5. **Every trip summary should be completely different** based on what was discussed

**Trip Summary Generation Process:**
1. Identify specific parks/locations mentioned
2. Note exact duration, budget, and group size discussed
3. Extract specific activities and interests mentioned
4. Generate highlights based on actual conversation topics
5. Create itinerary matching discussed preferences and locations

**Quality Check Before Returning:**
- ✅ Does title mention specific park(s) or region?
- ✅ Do highlights reflect actual conversation topics?
- ✅ Are activities based on discussed interests?
- ✅ Is duration/budget/group size from conversation?
- ❌ Are there any generic phrases like "Your Adventure" or "Multiple Parks"?

Return ONLY the JSON structure with conversation-specific details.`
        },
        {
          role: 'user',
          content: tripGenerationPrompt
        }
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'complete_trip_plan',
          schema: {
            type: 'object',
            properties: {
              title: { 
                type: 'string',
                description: 'Specific trip title with actual park names or region from conversation, NOT generic'
              },
              location: { 
                type: 'string',
                description: 'Specific park names or location from conversation, NOT "Multiple Parks"'
              },
              duration: { 
                type: 'string',
                description: 'Exact duration mentioned in conversation (e.g., "3 days, 2 nights")'
              },
              groupSize: { 
                type: 'string',
                description: 'Specific group size from conversation (e.g., "Family of 4")'
              },
              budget: { 
                type: 'string',
                description: 'Budget range from conversation or "Budget-friendly", "Moderate", "Luxury"'
              },
              highlights: {
                type: 'array',
                items: { type: 'string' },
                description: 'MUST be unique highlights based on actual conversation topics, activities, and interests discussed. NOT generic statements.'
              },
              featuredParks: { 
                type: 'array', 
                items: { type: 'string' },
                description: 'Actual park names from conversation'
              },
              activityTypes: { 
                type: 'array', 
                items: { type: 'string' },
                description: 'Specific activities mentioned in conversation'
              },
              estimatedCost: { type: 'string' },
              weatherForecast: { type: 'string' },
              accommodations: {
                type: 'object',
                properties: {
                  campgrounds: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        name: { type: 'string' },
                        location: { type: 'string' },
                        amenities: { type: 'array', items: { type: 'string' } },
                        cost: { type: 'string' },
                        reservationRequired: { type: 'boolean' },
                        description: { type: 'string' },
                        website: { type: 'string' },
                        phone: { type: 'string' }
                      }
                    }
                  },
                  cabins: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        name: { type: 'string' },
                        location: { type: 'string' },
                        capacity: { type: 'string' },
                        amenities: { type: 'array', items: { type: 'string' } },
                        cost: { type: 'string' },
                        reservationRequired: { type: 'boolean' },
                        website: { type: 'string' }
                      }
                    }
                  },
                  nearbyLodging: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        name: { type: 'string' },
                        type: { type: 'string' },
                        distance: { type: 'string' },
                        estimatedCost: { type: 'string' },
                        website: { type: 'string' }
                      }
                    }
                  }
                }
              },
              dining: {
                type: 'object',
                properties: {
                  onSite: { type: 'array', items: { type: 'string' } },
                  nearby: { type: 'array', items: { type: 'string' } },
                  groceryStores: { type: 'array', items: { type: 'string' } },
                  campKitchens: { type: 'boolean' }
                }
              },
              reservationInfo: {
                type: 'object',
                properties: {
                  website: { type: 'string' },
                  phone: { type: 'string' },
                  advanceBooking: { type: 'string' },
                  cancelPolicy: { type: 'string' },
                  officialParkWebsites: { 
                    type: 'array', 
                    items: {
                      type: 'object',
                      properties: {
                        parkName: { type: 'string' },
                        website: { type: 'string' },
                        reservationLink: { type: 'string' },
                        phone: { type: 'string' }
                      }
                    }
                  }
                }
              },
              itinerary: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    title: { type: 'string' },
                    date: { type: 'string' },
                    parkImage: { 
                      type: 'string',
                      description: 'Location-appropriate Unsplash URL matching specific park discussed'
                    },
                    parkImageAlt: { 
                      type: 'string',
                      description: 'Descriptive alt text for the specific park location'
                    },
                    parkWebsite: { type: 'string' },
                    activities: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          time: { 
                            type: 'string',
                            description: 'Format: "9:00 AM" or "14:30", NEVER "Morning/Afternoon/Evening"'
                          },
                          duration: { type: 'string' },
                          title: { type: 'string' },
                          description: { type: 'string' },
                          location: { type: 'string' },
                          activityType: { type: 'string' },
                          essentialItems: { type: 'array', items: { type: 'string' } }
                        },
                        required: ['time', 'title', 'description', 'location']
                      }
                    },
                    drivingDirections: { type: 'string' },
                    drivingTime: { type: 'string' },
                    distance: { type: 'string' }
                  },
                  required: ['title', 'date', 'parkImage', 'parkImageAlt', 'activities']
                }
              }
            },
            required: ['title', 'location', 'duration', 'groupSize', 'budget', 'highlights', 'featuredParks', 'activityTypes', 'estimatedCost', 'weatherForecast', 'accommodations', 'dining', 'reservationInfo', 'itinerary'],
            additionalProperties: false
          }
        }
      },
      max_tokens: 4000,
      temperature: 0.8,
    });

    const tripData = JSON.parse(response?.choices?.[0]?.message?.content);
    
    // **VALIDATION**: Ensure generated data is actually unique and conversation-specific
    const hasGenericContent = 
      tripData?.title?.includes('Your') || 
      tripData?.title?.includes('Adventure Plan') ||
      tripData?.location?.includes('Multiple Parks') ||
      tripData?.location?.includes('Based on our conversation');
    
    if (hasGenericContent) {
      console.warn('⚠️ Generated trip data contains generic content, attempting regeneration...');
      // Don't return null, but log warning for debugging
    }
    
    return tripData;
  } catch (error) {
    console.error('Error generating trip summary data:', error);
    return null;
  }
}

/**
 * Generates enhanced prompts for better user engagement
 * @param {Array} conversationHistory - Previous messages for context.
 * @returns {Promise<Array>} Array of suggested prompts.
 */
export async function generateSuggestedPrompts(conversationHistory = []) {
  try {
    if (conversationHistory?.length === 0) {
      // Default prompts for new conversations
      return [
        "I want to plan a family camping trip for this summer",
        "What are the best state parks for hiking and photography?",
        "Help me find budget-friendly state parks near me",
        "I'm looking for parks with swimming and water activities"
      ];
    }

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'Based on the conversation history, suggest 4 relevant follow-up questions the user might want to ask about state park trip planning. Make them specific and actionable.'
        },
        {
          role: 'user',
          content: `Based on this conversation, suggest 4 follow-up questions:
          
${conversationHistory?.slice(-6)?.map(msg => `${msg?.isUser ? 'User' : 'Assistant'}: ${msg?.content?.substring(0, 200)}...`)?.join('\n')}`
        }
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'suggested_prompts',
          schema: {
            type: 'object',
            properties: {
              prompts: { type: 'array', items: { type: 'string' }, minItems: 4, maxItems: 4 }
            },
            required: ['prompts'],
            additionalProperties: false
          }
        }
      },
      max_tokens: 500,
      temperature: 0.6,
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return result?.prompts;
  } catch (error) {
    console.error('Error generating suggested prompts:', error);
    
    // Fallback prompts
    return [
      "Tell me more about camping reservations and fees",
      "What should I pack for this type of trip?",
      "Are there any nearby attractions or restaurants?",
      "What's the best time of year to visit?"
    ];
  }
}